import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";

export default function ResourceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResource();
  }, [slug]);

  const loadResource = async () => {
    try {
      // Placeholder - implement when resources table exists
      toast.error("Ressource introuvable");
      navigate("/resources");
    } catch (error) {
      console.error("Error loading resource:", error);
      toast.error("Ressource introuvable");
      navigate("/resources");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!resource?.file_url) {
      toast.error("Aucun fichier disponible");
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("export_resource_pdf", {
        body: { resourceId: resource.id }
      });

      if (error) throw error;

      // Create download link
      const link = document.createElement("a");
      link.href = resource.file_url;
      link.download = `${resource.slug}.pdf`;
      link.click();

      toast.success("Téléchargement lancé !");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!resource) return null;

  return (
    <div className="min-h-dvh bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" onClick={() => navigate("/resources")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-primary/10 text-primary mb-2">
                  {resource.category}
                </div>
                <CardTitle className="text-3xl">{resource.title}</CardTitle>
                <p className="text-muted-foreground">{resource.description}</p>
              </div>
              {resource.file_url && (
                <Button onClick={handleDownload} className="gap-2">
                  <Download className="w-4 h-4" />
                  Télécharger
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div 
              className="prose dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resource.content) }}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
